import { Backdrop, CircularProgress, Grid } from "@mui/material";
import { IPicture, IProps } from "../../types/CommonTypes";
import Title from "../generic/Title";
import { fieldLabel } from "./sections";
import Item from "./generic/ImageItem";
import { useEffect, useState } from "react";
import useFirebase from "../../commons/firebase/useFirebase";
import TextItem from "./generic/TextItem";
import { getRandomId } from "../../commons/constants/helpers";
import { toast } from "react-toastify";

export default function DashboardAbout({ item, onChange }: IProps) {
	const maxCarouselItems = 5;

	// Inicializar el carrusel con un máximo de 5 elementos, cada uno con un ID único
	const initializeCarousel = (carrusel: IPicture[] = []) => {
		return carrusel.length >= maxCarouselItems
			? carrusel
			: [
					...carrusel,
					...Array(maxCarouselItems - carrusel.length)
						.fill(null)
						.map(() => ({
							id: getRandomId(),
							name: "",
							url: "",
							path: "",
						})),
			  ];
	};

	const [data, setData] = useState({
		...item,
		carrusel: initializeCarousel(item.carrusel || []),
	});

	const { uploadFileToStorage, deletePicture, loading } = useFirebase();

	useEffect(() => {
		onChange && onChange(data);
	}, [data]);

	const handleNewPic = async (id: string, file: any) => {
		try {
			let path = "/assets/about/";
			const downloadURL = await uploadFileToStorage(
				file,
				path + file.name
			);
			let updated = data.images.map((item) => {
				if (item.id === id) {
					return {
						...item,
						name: file.name,
						path: path,
						url: downloadURL,
					};
				}
				return item;
			});

			setData({ ...data, images: updated });
		} catch (error) {
			console.log("Error al cargar el archivo:", error);
		}
	};

	const handleNewCarouselPic = async (id: string, file: any) => {
		await handleDeleteCarouselPic(id);
		try {
			let path = "/assets/about/";
			const fileName = "ABOUT_" + getRandomId();
			const downloadURL = await uploadFileToStorage(
				file,
				path + fileName
			);
			let updated = data.carrusel.map((item) => {
				if (item.id === id) {
					return {
						...item,
						name: fileName,
						path: path + fileName,
						url: downloadURL,
					};
				}
				return item;
			});

			setData({ ...data, carrusel: updated });
		} catch (error) {
			console.log("Error al cargar el archivo:", error);
		}
	};

	const handleDeleteCarouselPic = async (id: string) => {
		const path = data.carrusel.find((item) => item.id === id)?.path;
		if (!path) return;
		await deletePicture(path, (res) => {
			if (res) {
				let updated = data.carrusel
					.filter((item) => item.id !== id)
					.map((item, index) => ({
						...item,
						name: "ABOUT_" + getRandomId(),
					}));

				setData({ ...data, carrusel: initializeCarousel(updated) });
			} else {
				toast.error("Error al eliminar la imagen");
			}
		});
	};

	const handleTextChange = (id: string, value: string) => {
		let updated = data.texts.map((item) => {
			if (item.id === id) {
				return {
					...item,
					text: value,
				};
			}
			return item;
		});

		setData({ ...data, texts: updated });
	};

	return (
		<Grid container>
			<Title>Imágenes</Title>

			{data.images.map((image) => (
				<Item
					key={image.id}
					label={fieldLabel("about", image.id)}
					source={image.url}
					onUpdate={(file: any) => handleNewPic(image.id, file)}
				/>
			))}

			<Title>Carrusel de fotos</Title>
			{data.carrusel.map((image, index) => (
				<Item
					key={image.id}
					label={`Foto ${index + 1} del carrusel`}
					source={image.url}
					onUpdate={(file: any) =>
						handleNewCarouselPic(image.id, file)
					}
					onDelete={() => handleDeleteCarouselPic(image.id)}
				/>
			))}

			<Title>Textos</Title>
			{data.texts.map((item) => (
				<TextItem
					key={item.id}
					id={item.id}
					label={fieldLabel("about", item.id)}
					text={item.text}
					handleChange={(value: string) =>
						handleTextChange(item.id, value)
					}
				/>
			))}
			<Backdrop open={loading}>
				<CircularProgress color="primary" />
			</Backdrop>
		</Grid>
	);
}
